import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import { SOROperationalPlanTable } from './SOROperationalPlans.MUITable'
import {
  useSOROperationalPlans,
  useApproveSOROperationalPlan,
} from './SOROperationalPlans.data'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { SOROperationaPlanData } from './SOROperationalPlans.types'
import { useQueryClient } from 'react-query'
import toast from 'react-hot-toast'

type ApprovalDialogProps = {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

const ApprovalDialog = ({
  open,
  onConfirm,
  onCancel,
  isLoading,
}: ApprovalDialogProps) => {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Approve Plan</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Approving the plan will send planned orders to the system of record.
          Do you want to proceed?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="primary" disabled={isLoading}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const SOROperationalPlanView = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading } = useSOROperationalPlans()
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] =
    useState<SOROperationaPlanData | null>(null)

  const { mutate: approvePlan, isLoading: isApproving } =
    useApproveSOROperationalPlan(
      () => {
        setApprovalDialogOpen(false)
        setSelectedPlan(null)
        queryClient.invalidateQueries(['SOROperationalPlans'])
        toast.success('Operational plan approved successfully.')
      },
      () => {
        toast.error(
          'Failed to approve the operational plan. Please try again later.'
        )
      }
    )

  const handleRowClick = (selectedMonday: string) => {
    navigate(`/operational-plan/${selectedMonday}`)
  }

  const handleApprovePlan = (plan: SOROperationaPlanData) => {
    setSelectedPlan(plan)
    setApprovalDialogOpen(true)
  }

  const handleApprovalConfirm = () => {
    if (selectedPlan) {
      approvePlan(selectedPlan.planId)
    }
  }

  const handleApprovalCancel = () => {
    setApprovalDialogOpen(false)
    setSelectedPlan(null)
  }

  return (
    <Box>
      <SOROperationalPlanTable
        isLoading={isLoading}
        data={data ?? []}
        handleRowClick={handleRowClick}
        handleApprovePlan={handleApprovePlan}
      />

      <ApprovalDialog
        open={approvalDialogOpen}
        onConfirm={handleApprovalConfirm}
        onCancel={handleApprovalCancel}
        isLoading={isApproving}
      />
    </Box>
  )
}
